import { Link } from '@tanstack/react-router';
import { type ColumnDef, createColumnHelper } from '@tanstack/table-core';
import { PenIcon, Trash2Icon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { TableHeader } from '@/components/DataTable/TableHeader';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { AwsCredentialsForList } from '@/lib/queries/integrations/cloud/aws/credentials';
import type { AwsRole } from '@/lib/queries/integrations/cloud/aws/roles';

const rolesColumnHelper = createColumnHelper<AwsRole>();
const usersColumnHelper = createColumnHelper<AwsCredentialsForList>();

export const getRolesColumns = (
  deleteRole: (id: string) => Promise<void>,
): ColumnDef<AwsRole>[] =>
  [
    rolesColumnHelper.accessor('name', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.aws.roles.table"
          type="sort-only"
        />
      ),
      size: 50,
    }),
    rolesColumnHelper.accessor('status', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          optionsTranslationPath="integrations.aws.status"
          headersTranslationPath="integrations.aws.roles.table"
          type="select"
        />
      ),
      cell: ({ getValue }) => {
        const status = getValue();

        switch (status) {
          case 'inactive':
            return <FormattedMessage id="integrations.aws.status.inactive" />;
          case 'active':
            return <FormattedMessage id="integrations.aws.status.active" />;
          case 'error':
            return (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p className="underline decoration-dotted">
                      <FormattedMessage id="integrations.aws.status.error" />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[400px]">
                    <FormattedMessage id="integrations.aws.roleError" />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
        }
      },
      size: 30,
    }),
    rolesColumnHelper.accessor('id', {
      header: '',
      id: 'edit',
      cell: ({ getValue }) => (
        <Link
          to="/integrations/aws/roles/$roleId"
          params={{ roleId: getValue() }}
        >
          <Button variant="link" className="text-xs">
            <FormattedMessage id="integrations.aws.roles.table.edit" />
            <PenIcon size={16} className="ml-1" />
          </Button>
        </Link>
      ),
      size: 10,
    }),
    rolesColumnHelper.accessor('id', {
      header: '',
      id: 'delete',
      cell: ({ getValue }) => (
        <Button
          variant="link"
          onClick={() => deleteRole(getValue())}
          className="text-xs"
        >
          <FormattedMessage id="integrations.aws.roles.table.delete" />
          <Trash2Icon size={16} className="ml-1" />
        </Button>
      ),
      size: 10,
    }),
  ] as ColumnDef<AwsRole>[];

export const getUsersColumns = (
  deleteUser: (id: string) => Promise<void>,
): ColumnDef<AwsCredentialsForList>[] =>
  [
    usersColumnHelper.accessor('name', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.aws.users.table"
          type="sort-only"
        />
      ),
      size: 50,
    }),
    rolesColumnHelper.accessor('status', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.aws.users.table"
          optionsTranslationPath="integrations.aws.status"
          type="select"
        />
      ),
      cell: ({ getValue }) => {
        const status = getValue();

        switch (status) {
          case 'inactive':
            return <FormattedMessage id="integrations.aws.status.inactive" />;
          case 'active':
            return <FormattedMessage id="integrations.aws.status.active" />;
          case 'error':
            return (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p className="underline decoration-dotted">
                      <FormattedMessage id="integrations.aws.status.error" />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[400px]">
                    <FormattedMessage id="integrations.aws.userError" />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
        }
      },
      size: 30,
    }),
    usersColumnHelper.accessor('id', {
      header: '',
      id: 'edit',
      cell: ({ getValue }) => (
        <Link
          to="/integrations/aws/users/$userId"
          params={{ userId: getValue() }}
        >
          <Button variant="link" className="text-xs">
            <FormattedMessage id="integrations.aws.users.table.edit" />
            <PenIcon size={16} className="ml-1" />
          </Button>
        </Link>
      ),
      size: 10,
    }),
    usersColumnHelper.accessor('id', {
      header: '',
      id: 'delete',
      cell: ({ getValue }) => (
        <Button
          variant="link"
          onClick={() => deleteUser(getValue())}
          className="text-xs"
        >
          <FormattedMessage id="integrations.aws.users.table.delete" />
          <Trash2Icon size={16} className="ml-1" />
        </Button>
      ),
      size: 10,
    }),
  ] as ColumnDef<AwsCredentialsForList>[];
